/**
 * Le modèle anatomique de la carte corporelle — **repris, pas dessiné**
 * *(06/09/2026)*.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * PROVENANCE ET LICENCE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Polygones repris de **react-body-highlighter**
 * (https://github.com/giavinh79/react-body-highlighter), lui-même dérivé de
 * `react-native-body-highlighter`.
 *
 *     MIT License — Copyright (c) 2020 GV79
 *
 *     Permission is hereby granted, free of charge, to any person obtaining a
 *     copy of this software and associated documentation files (the
 *     "Software"), to deal in the Software without restriction, including
 *     without limitation the rights to use, copy, modify, merge, publish,
 *     distribute, sublicense, and/or sell copies of the Software, and to
 *     permit persons to whom the Software is furnished to do so, subject to
 *     the following conditions:
 *
 *     The above copyright notice and this permission notice shall be included
 *     in all copies or substantial portions of the Software.
 *
 *     THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
 *     OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 *     MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 *     IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 *     CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 *     TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 *     SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * 🔴 **Licence vérifiée par l'API GitHub**, pas lue dans un README — c'est le
 * contrôle que le §1.1 exige nommément, et celui qui a été fait pour le jeu
 * d'exercices le 27/08/2026 :
 *
 *     curl -s https://api.github.com/repos/giavinh79/react-body-highlighter/license
 *     → "spdx_id": "MIT"
 *
 * ⚠️ **MIT n'est pas l'Unlicense** : contrairement au jeu d'exercices, elle
 * **exige** que l'avis de copyright voyage avec le code. C'est pourquoi il est
 * recopié ci-dessus en entier et non résumé — le retirer rendrait l'emploi
 * non conforme.
 *
 * ⚠️ **Repris en données, pas en dépendance.** Installer le paquet aurait
 * apporté un composant React, ses propres conventions de rendu et une surface
 * de mise à jour, là où on n'a besoin que de la géométrie. Le §1.1 demande de
 * vérifier qu'une dépendance survit au VPS ; celle-ci n'a simplement pas lieu
 * d'exister.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **La correspondance vers les muscles FRANÇAIS est une relation N–N**, et
 * c'est l'anatomie qui le veut : les épaules ont une région de face et une de
 * dos ; le trapèze et le grand dorsal couvrent tous deux ce que le référentiel
 * appelle « milieu du dos ». Une région porte donc une **liste** de muscles, et
 * la carte peint son intensité **maximale**.
 *
 * ⚠️ **Tête et genoux ne portent aucun muscle** : ils existent pour que la
 * figure soit un corps, et restent neutres. C'est la règle déjà tenue par la
 * silhouette qu'ils remplacent — « tête, mains et pieds sont neutres, aucun
 * muscle du jeu ne leur correspond ».
 *
 * 🔴 **Fichier GÉNÉRÉ, à ne pas retoucher à la main.** Les coordonnées
 * viennent telles quelles de la source ; les corriger ici ferait diverger la
 * figure de ce qui est attribué ci-dessus.
 */

/** Une région du modèle, et les muscles du référentiel qu'elle représente. */
export interface RegionCorps {
  /** Le nom d'origine dans la source — sert de clé de rendu et de diagnostic. */
  cle: string;
  /** Muscles français concernés. Vide = pièce neutre (tête, genoux). */
  muscles: string[];
  /** Points de polygone SVG, dans le cadre `VIEWBOX`. */
  polygones: string[];
}

/** Le cadre réel du modèle, mesuré sur tous les points des deux vues. */
export const VIEWBOX = '0 0 1000 2200';

export const FACE: RegionCorps[] = [
  {
    cle: 'CHEST',
    muscles: ['pectoraux'],
    polygones: [
      '518 416 510 551 580 580 678 555 706 473 620 416',
      '298 465 314 555 408 580 482 551 478 420 376 420',
    ],
  },
  {
    cle: 'OBLIQUES',
    muscles: ['abdominaux'],
    polygones: [
      '686 633 673 571 588 596 600 641 604 833 657 788 665 698',
      '339 784 331 718 310 633 322 571 408 592 392 633 392 837',
    ],
  },
  {
    cle: 'ABS',
    muscles: ['abdominaux'],
    polygones: [
      '563 592 580 641 584 780 584 927 563 984 551 1041 514 1078 510 845 506 673 510 571',
      '437 588 486 571 490 673 486 845 482 1073 445 1037 408 914 408 784 412 645',
    ],
  },
  {
    cle: 'BICEPS',
    muscles: ['biceps'],
    polygones: [
      '167 682 180 714 229 661 290 539 278 494 204 559',
      '714 494 702 547 763 661 816 718 829 690 788 555',
    ],
  },
  {
    cle: 'TRICEPS',
    muscles: ['triceps'],
    polygones: [
      '694 555 694 616 759 727 776 702 755 673',
      '224 694 298 555 298 608 229 731',
    ],
  },
  {
    cle: 'NECK',
    muscles: ['cou'],
    polygones: [
      '555 237 506 335 506 392 616 400 706 449 694 367 633 351 584 306',
      '290 449 302 371 363 351 412 302 445 245 490 339 486 392 380 396',
    ],
  },
  {
    cle: 'FRONT_DELTOIDS',
    muscles: ['épaules'],
    polygones: [
      '784 531 796 478 792 412 759 380 710 363 722 429 714 473',
      '282 473 212 531 200 478 204 408 245 371 286 371 269 433',
    ],
  },
  {
    cle: 'HEAD',
    muscles: [],
    polygones: [
      '424 29 400 118 420 196 461 233 498 253 547 224 576 192 592 102 571 24 498 0',
    ],
  },
  {
    cle: 'ABDUCTORS',
    muscles: ['abducteurs'],
    polygones: [
      '527 1102 543 1249 600 1102 620 1000 649 943 600 927 567 1045',
      '478 1106 449 1253 420 1159 404 1131 396 1073 380 1024 347 939 396 922 416 992 437 1053',
    ],
  },
  {
    cle: 'QUADRICEPS',
    muscles: ['quadriceps'],
    polygones: [
      '347 988 371 1082 371 1278 343 1371 310 1327 294 1200 282 1114 294 1008 322 947',
      '633 1057 645 1000 669 947 702 1012 710 1118 682 1331 653 1376 624 1286 620 1114',
      '388 1294 384 1122 412 1184 445 1294 429 1351 400 1461 363 1465 355 1400',
      '596 1457 555 1290 608 1139 612 1302 641 1396 629 1465',
      '327 1384 265 1457 257 1367 257 1273 269 1143 294 1335',
      '718 1131 739 1241 739 1404 727 1457 665 1384 702 1335',
    ],
  },
  {
    cle: 'KNEES',
    muscles: [],
    polygones: [
      '339 1400 347 1433 355 1473 363 1510 351 1567 298 1567 273 1527 273 1473 302 1441',
      '657 1400 722 1478 722 1522 698 1571 649 1567 629 1510',
    ],
  },
  {
    cle: 'CALVES',
    muscles: ['mollets'],
    polygones: [
      '714 1604 735 1535 767 1612 796 1678 784 1878 796 1955 747 1955',
      '249 1947 278 1649 282 1604 261 1543 249 1576 224 1616 208 1678 220 1882 208 1955',
      '727 1951 698 1592 653 1584 641 1624 641 1653 657 1771',
      '355 1584 359 1624 359 1669 351 1722 351 1767 322 1820 306 1873 269 1947 273 1878 282 1804 286 1755 290 1698 298 1641 302 1588',
    ],
  },
  {
    cle: 'FOREARM',
    muscles: ['avant-bras'],
    polygones: [
      '61 886 102 751 147 702 163 743 192 735 45 976 0 1000',
      '845 698 833 735 800 731 951 984 1000 1004 935 894 898 763',
      '776 722 776 776 804 841 853 898 922 1012 947 996',
      '69 1012 135 906 188 841 216 771 212 718 49 988',
    ],
  },
];

export const DOS: RegionCorps[] = [
  {
    cle: 'HEAD',
    muscles: [],
    polygones: [
      '506 0 460 9 409 55 404 128 451 200 557 200 591 136 596 47 557 13',
    ],
  },
  {
    cle: 'TRAPEZIUS',
    muscles: ['trapèzes', 'milieu du dos'],
    polygones: [
      '447 217 477 217 472 383 477 647 383 532 353 409 311 366 391 332 438 272',
      '523 217 557 217 566 272 609 328 689 366 647 404 617 532 523 647 532 383',
    ],
  },
  {
    cle: 'BACK_DELTOIDS',
    muscles: ['épaules'],
    polygones: [
      '294 370 230 391 174 443 183 536 243 494 272 464',
      '711 370 783 396 826 447 817 536 749 489 723 451',
    ],
  },
  {
    cle: 'UPPER_BACK',
    muscles: ['grand dorsal', 'milieu du dos'],
    polygones: [
      '311 387 281 489 285 553 340 753 472 711 472 664 366 540 336 413',
      '689 387 719 494 715 562 660 753 528 711 528 664 634 545 664 417',
    ],
  },
  {
    cle: 'TRICEPS',
    muscles: ['triceps'],
    polygones: [
      '268 498 179 557 145 723 166 817 217 638 268 557',
      '736 502 821 557 860 732 834 821 779 630 732 557',
      '268 583 268 685 230 753 191 774 226 655',
      '728 583 770 647 804 774 766 753 728 689',
    ],
  },
  {
    cle: 'LOWER_BACK',
    muscles: ['bas du dos'],
    polygones: [
      '477 728 345 770 353 834 494 1021 468 830',
      '523 728 655 770 647 834 506 1021 532 838',
    ],
  },
  {
    cle: 'FOREARM',
    muscles: ['avant-bras'],
    polygones: [
      '864 757 911 834 932 940 1000 1064 962 1043 881 894 843 838',
      '136 757 89 838 68 936 0 1064 38 1043 123 885 157 830',
      '813 796 774 779 791 847 911 1038 932 1089 945 1047',
      '187 796 221 779 209 843 94 1030 68 1085 51 1047',
    ],
  },
  {
    cle: 'GLUTEAL',
    muscles: ['fessiers'],
    polygones: [
      '447 996 302 1085 298 1187 315 1260 472 1213 494 1149',
      '553 991 511 1145 523 1209 681 1260 698 1191 694 1085',
    ],
  },
  {
    cle: 'ADDUCTOR',
    muscles: ['adducteurs'],
    polygones: [
      '481 1230 447 1230 413 1255 451 1443 485 1357 489 1294',
      '519 1226 557 1234 591 1260 549 1443 519 1362 511 1294',
    ],
  },
  {
    cle: 'HAMSTRING',
    muscles: ['ischio-jambiers'],
    polygones: [
      '289 1221 311 1294 366 1260 353 1353 345 1502 294 1583 289 1468 277 1413 272 1315',
      '715 1217 694 1289 638 1260 655 1366 664 1502 711 1583 715 1477 728 1421 736 1319',
      '387 1255 443 1460 404 1668 362 1528 370 1353',
      '617 1255 634 1362 643 1532 600 1668 562 1464',
    ],
  },
  {
    cle: 'KNEES',
    muscles: [],
    polygones: [
      '345 1532 311 1591 336 1664 374 1626',
      '664 1536 630 1630 668 1664 694 1591',
    ],
  },
  {
    cle: 'CALVES',
    muscles: ['mollets'],
    polygones: [
      '294 1604 285 1672 247 1796 238 1928 255 1970 285 1932 298 1800 319 1711 319 1668',
      '374 1651 353 1677 332 1719 311 1804 302 1919 340 2000 387 1906 391 1689',
      '630 1651 613 1685 617 1906 664 1996 706 1919 689 1796 668 1702',
      '706 1604 723 1685 757 1791 766 1928 745 1966 723 1936 706 1796 681 1681',
    ],
  },
  {
    cle: 'LEFT_SOLEUS',
    muscles: ['mollets'],
    polygones: [
      '285 1957 302 1957 336 2017 306 2200 285 2136 268 1983',
    ],
  },
  {
    cle: 'RIGHT_SOLEUS',
    muscles: ['mollets'],
    polygones: [
      '698 1957 719 1957 736 1983 719 2132 702 2196 672 2021',
    ],
  },
];
